import React, { useState, useEffect, useCallback, useContext } from 'react';
import RoomContext from '../RoomContext';
import { useRoomApi } from '../hooks/useRoomApi';

// Per-activity target temperatures. The backend stores one row per activity name; an activity with no
// row falls back to the room's default active temperature, so only saved overrides are listed here.
// Calendar events themselves are managed in Google Calendar, not here.
const ScheduleEditor = () => {
  const { tenant, room } = useContext(RoomContext);
  const api = useRoomApi(tenant, room);
  const [activities, setActivities] = useState(null);
  const [error, setError] = useState(null);
  const [newName, setNewName] = useState('');
  const [newTemp, setNewTemp] = useState('');

  const load = useCallback(async () => {
    try {
      setActivities(await api.getActivities());
      setError(null);
    } catch (e) {
      setError('No se pudieron cargar las actividades.');
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const save = async (name, temp) => {
    if (name === '' || temp === '' || Number.isNaN(Number(temp))) return;
    try { await api.setActivityTemperature(name, Number(temp)); } catch (e) { setError('No se pudo guardar.'); }
    await load();
  };

  const clear = async (name) => {
    try { await api.clearActivityTemperature(name); } catch (e) { setError('No se pudo borrar.'); }
    await load();
  };

  const addNew = async () => {
    await save(newName.trim(), newTemp);
    setNewName('');
    setNewTemp('');
  };

  if (error && !activities) return <div className="text-danger small">{error}</div>;
  if (!activities) return <div className="small text-muted">Cargando…</div>;

  return (
    <>
      {activities.length === 0 && (
        <p className="small text-muted mb-2">Sin temperaturas guardadas; cada actividad usa la temp. activa por defecto.</p>
      )}
      {activities.map((a) => (
        <ActivityRow key={a.name} name={a.name} temp={a.desiredTemperature} onSave={save} onClear={clear} />
      ))}

      <div className="row g-2 align-items-end mt-2 pt-2 border-top">
        <div className="col-6">
          <label className="form-label small mb-1">Nueva actividad</label>
          <input className="form-control form-control-sm" value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Nombre" />
        </div>
        <div className="col-3">
          <label className="form-label small mb-1">°C</label>
          <input type="number" step={0.5} className="form-control form-control-sm" value={newTemp} onChange={(e) => setNewTemp(e.target.value)} />
        </div>
        <div className="col-3">
          <button className="btn btn-outline-primary btn-sm w-100" onClick={addNew} disabled={!newName.trim() || newTemp === ''}>Agregar</button>
        </div>
      </div>
      {error && <div className="text-danger small mt-2">{error}</div>}
    </>
  );
};

const ActivityRow = ({ name, temp, onSave, onClear }) => {
  const [value, setValue] = useState(temp);
  useEffect(() => { setValue(temp); }, [temp]);

  return (
    <div className="row g-2 align-items-center mb-2">
      <div className="col-6"><span className="small">{name}</span></div>
      <div className="col-3">
        <input
          type="number"
          step={0.5}
          className="form-control form-control-sm"
          value={value}
          onChange={(e) => setValue(e.target.value === '' ? '' : Number(e.target.value))}
          onBlur={() => { if (value !== '' && value !== temp) onSave(name, value); }}
        />
      </div>
      <div className="col-3">
        <button className="btn btn-outline-secondary btn-sm w-100" onClick={() => onClear(name)}>Quitar</button>
      </div>
    </div>
  );
};

export default ScheduleEditor;
